import type { BadgeVariant, SelectOption } from '@/types/common.types';
import type { FilterConfig } from '@/types/filters.types';
import type { ManagementSearchType, PackageManagementStats } from '@/services/packages-management.service';

/**
 * Filter keys MUST match the canonical legacy filter registry.
 * DO NOT rename keys or change option values — dynamic lists store these permanently.
 * See lib/filterRegistry.ts for the full registry and rules.
 */
export const PACKAGE_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search packages...' },
  { key: 'daterange', label: 'Date', type: 'daterange' },
  { key: 'package-type', label: 'Package', type: 'select', allLabel: 'All packages', multi: true },
  { key: 'package-status', label: 'Status', type: 'select', allLabel: 'All statuses', multi: true },
  { key: 'package-remaining', label: 'Remaining Products', type: 'select', allLabel: 'All packages', multi: true },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
];

export const PACKAGE_SEARCH_OPTIONS: SelectOption<ManagementSearchType>[] = [
  { value: '0', label: 'Name' },
  { value: '1', label: 'Plan ID' },
  { value: '2', label: 'Patient ID' },
  { value: '5', label: 'Pymt. ID' },
  { value: '3', label: 'Amount' },
  { value: '4', label: 'User' },
];

/**
 * Re-exported, not redeclared — same story as MEMBERSHIP_STATUS_OPTIONS next
 * door. This was a verbatim copy of filterRegistry's PACKAGE_STATUS_INDEX
 * ('0' Active, '1' Declined, '2' Inactive) typed as
 * `SelectOption<PackageFilterStatus>[]`, and PackageFilterStatus is the word
 * vocabulary while these values are the legacy indices. All three were
 * unassignable and had been reported as such.
 */
export { PACKAGE_REMAINING_OPTIONS, PACKAGE_STATUS_OPTIONS } from '@/lib/filterRegistry';

export const PACKAGE_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  active: 'success',
  declined: 'warning',
  inactive: 'neutral',
};

export const EMPTY_PACKAGE_STATS: PackageManagementStats = {
  activeCount: 0,
  declinedCount: 0,
  inactiveCount: 0,
};

function parseDateSafe(value: string | null | undefined): Date | null {
  if (!value) return null;
  if (value === '0000-00-00' || value === '0000-00-00 00:00:00') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatPackageDate(value: string | null | undefined): { monthDay: string; year: string } | null {
  const date = parseDateSafe(value);
  if (!date) return null;
  return {
    monthDay: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    year: String(date.getFullYear()),
  };
}

export function formatPackageDateInline(value: string | null | undefined): string {
  const date = parseDateSafe(value);
  if (!date) return '\u2014';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatPackageCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

export function packageStatusLabel(status: string): string {
  switch (status) {
    case 'declined':
      return 'Declined';
    case 'inactive':
      return 'Inactive';
    default:
      return 'Active';
  }
}
